import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { Button, Cushion, Typography, Rectangle, GridItem, Space } from '@pitchero/react-ui';
import { uniq } from 'lodash';
import { Trans } from '../../../lib/i18n';

import Variant from './variant';
import LoginNotice from './login-notice';
import MessagesLink from './messages-link';
import JoinClubPanel from './join-club-panel';
import ProductUnavailable from './product-unavailable';

class VariantSelector extends Component {
  constructor(props) {
    super(props);

    const inStockVariants = this.getInStockVariants();
    let initialSelectedVariantId = '';

    if (inStockVariants[0] !== undefined && inStockVariants[0].id) {
      initialSelectedVariantId = inStockVariants[0].id;
    }

    this.state = {
      selectedVariantId: initialSelectedVariantId,
      panelOpen: false,
    };
  }

  getInStockVariants() {
    const {
      product: { variants },
    } = this.props;

    return variants.filter((variant) => !variant.outOfStock);
  }

  getOutOfStockVariants() {
    const {
      product: { variants },
    } = this.props;

    return variants.filter((variant) => variant.outOfStock);
  }

  buyProduct = () => {
    const { isLoggedIn, addToBasket } = this.props;
    const { selectedVariantId } = this.state;

    if (isLoggedIn) {
      addToBasket(selectedVariantId);
    } else {
      this.setState({ panelOpen: true });
    }
  };

  hasVariantsOutOfStock() {
    return this.getOutOfStockVariants().length > 0;
  }

  productOutOfStock() {
    return this.getInStockVariants().length === 0;
  }

  renderInStockVariants() {
    const variants = this.getInStockVariants();

    return this.renderListOfVariants(variants, true);
  }

  renderListOfVariants(variants, inStock = true) {
    // We only include the tagging description on each variant if they differ.
    // Here we check to see if the min & max vary between variants.
    const includeTaggingDescription =
      uniq(variants.map((v) => `min${v.minTaggedMembers}-max${v.maxTaggedMembers}`)).length > 1;

    if (variants.length === 1 && inStock) {
      const variant = variants[0];

      return (
        <Variant
          key={variant.id}
          includeTaggingDescription={includeTaggingDescription}
          selected
          inStock
          onSelect={() => this.setState({ selectedVariantId: variant.id })}
          {...variant}
        />
      );
    }
    const { selectedVariantId } = this.state;

    return variants.map((variant) => (
      <Variant
        key={variant.id}
        includeTaggingDescription={includeTaggingDescription}
        selected={selectedVariantId === variant.id}
        inStock={inStock}
        onSelect={() => this.setState({ selectedVariantId: variant.id })}
        {...variant}
      />
    ));
  }

  renderOutOfStockVariants() {
    if (!this.hasVariantsOutOfStock() || this.productOutOfStock()) {
      return null;
    }

    return this.renderListOfVariants(this.getOutOfStockVariants(), false);
  }

  render() {
    const { isLoggedIn, product } = this.props;

    if (!product.canBePurchased || product.outOfStock) {
      return <ProductUnavailable />;
    }

    if (!isLoggedIn && product.requiresAssignment) {
      return <LoginNotice />;
    }

    if (product.requiresAssignment && !product.assignment) {
      return (
        <Cushion all="medium">
          <Rectangle fill="mercury" radius="rounded">
            <Typography preset="subtitle--small" component="div">
              <Trans i18nKey="shop:product_not_assigned">
                Sorry, looks like you&apos;re not assigned to buy this product
              </Trans>
            </Typography>
            <Space top="xsmall">
              <Typography preset="description" component="div" color="dovegrey">
                <Trans i18nKey="shop:product_not_assigned_description">
                  For more information please
                  <MessagesLink>contact</MessagesLink>
                  your club admin
                </Trans>
              </Typography>
            </Space>
          </Rectangle>
        </Cushion>
      );
    }

    const { addToBasket } = this.props;
    const { selectedVariantId, panelOpen } = this.state;

    return (
      <>
        {this.productOutOfStock() === true && (
          <Typography component="div" color="dovegrey">
            <Trans i18nKey="shop:product_out_of_stock">
              This product is out of stock and cannot be purchased.
            </Trans>
          </Typography>
        )}

        <GridItem>
          {this.renderInStockVariants()}
          {this.renderOutOfStockVariants()}
        </GridItem>

        {this.productOutOfStock() === false && (
          <Button theme="primary" onClick={this.buyProduct}>
            <Trans i18nKey="common:buy">Buy</Trans>
          </Button>
        )}

        <JoinClubPanel
          isOpen={panelOpen}
          addToBasket={() => addToBasket(selectedVariantId)}
          onClose={() => this.setState({ panelOpen: false })}
          joinParams={{ variant_id: selectedVariantId }}
        />
      </>
    );
  }
}

VariantSelector.propTypes = {
  addToBasket: PropTypes.func.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  product: PropTypes.shape({
    id: PropTypes.number,
    category: PropTypes.string,
    description: PropTypes.string,
    name: PropTypes.string,
    assignment: PropTypes.shape(),
    canBePurchased: PropTypes.bool,
    outOfStock: PropTypes.bool,
    requiresAssignment: PropTypes.bool, // Does the user need to have been assigned to the product to purchase.
    images: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        url: PropTypes.string,
      }),
    ),
    variants: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        label: PropTypes.string,
        paymentType: PropTypes.string,
        setupFee: PropTypes.string,
        amountDescription: PropTypes.string,
        amount: PropTypes.string,
        currencyCode: PropTypes.string,
        totalPayable: PropTypes.string,
      }),
    ),
  }).isRequired,
};

export default VariantSelector;
